/**
 * Market View
 * Places where players SELL fish and access quest boards
 * Organized by zone for clarity
 */

import { useState, useEffect, useMemo } from 'react';
import { useGameStore } from '@/state';
import { marketManager } from '@/systems';
import { MarketDefinition } from '@/types';
import { formatCurrency } from '@/data';
import { ZONE_DEFINITIONS } from '@/data/zones/zoneData';
import { QuestBoardView } from './QuestBoardView';
import { QuestSellPanel } from '../quests/QuestSellPanel';
import { MarketSellView } from './MarketSellView';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { BackButton } from '@/components/layout/BackButton';
import { EmptyState } from '@/components/layout/EmptyState';
import { DetailView } from '@/components/layout/DetailView';

interface MarketCardProps {
  market: MarketDefinition;
  onVisit: (marketId: string, view: 'quests' | 'sell') => void;
}

function MarketCard({ market, onVisit }: MarketCardProps) {
  const isUnlocked = marketManager.isMarketUnlocked(market.id);
  const canUnlock = marketManager.canUnlockMarket(market.id);

  const handleUnlock = () => {
    marketManager.unlockMarket(market.id);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 border-2 border-gray-700">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white">{market.name}</h3>
          <p className="text-gray-400 text-sm">{market.description}</p>
          {market.npcName && (
            <p className="text-gray-500 text-xs mt-1">Vendor: {market.npcName}</p>
          )}
        </div>
        <div className="text-3xl">🍽️</div>
      </div>

      {/* Market Info */}
      <div className="bg-gray-900 rounded p-3 mb-3">
        <div className="text-xs space-y-1">
          <p className="text-gray-400">
            💰 Sells for {Math.round(market.fishPriceMultiplier * 100)}% of base price
          </p>
          <p className="text-gray-400">
            📋 {market.questBoard.maxActiveQuests} quests available
          </p>
        </div>
      </div>

      {!isUnlocked ? (
        <div className="mt-4">
          <div className="bg-red-900/20 rounded p-3 mb-3 border border-red-800">
            <p className="text-sm text-red-400">🔒 Locked</p>
            {market.unlockRequirements && (
              <div className="text-xs text-gray-500 mt-2">
                {market.unlockRequirements.minCurrency && (
                  <p>Requires: {formatCurrency(market.unlockRequirements.minCurrency)}</p>
                )}
                {market.unlockRequirements.minLevel && (
                  <p>Requires Level: {market.unlockRequirements.minLevel}</p>
                )}
                {market.unlockRequirements.completedQuests && (
                  <p>Complete specific quests</p>
                )}
              </div>
            )}
          </div>
          {canUnlock && (
            <button
              onClick={handleUnlock}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold transition-colors"
            >
              Unlock Market
            </button>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <div className="bg-green-900/20 rounded p-3 mb-3 border border-green-800">
            <p className="text-sm text-green-400">✓ Unlocked</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                marketManager.visitMarket(market.id);
                onVisit(market.id, 'sell');
              }}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold transition-colors"
            >
              💰 Sell Fish
            </button>
            <button
              onClick={() => {
                marketManager.visitMarket(market.id);
                onVisit(market.id, 'quests');
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold transition-colors"
            >
              📋 Quests
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MarketView() {
  const markets = marketManager.getAllMarkets();
  const unlockedZones = useGameStore((state) => state.playerState.unlockedZones);
  const targetMarketId = useGameStore((state) => state.targetMarketId);
  const activeQuestSell = useGameStore((state) => state.activeQuestSell);

  // Auto-enter the single market when only 1 is unlocked
  const unlockedMarkets = useMemo(
    () => markets.filter((m) => marketManager.isMarketUnlocked(m.id)),
    [markets]
  );
  const autoEnterId = unlockedMarkets.length === 1 ? unlockedMarkets[0].id : null;

  const [visitingMarketId, setVisitingMarketId] = useState<string | null>(autoEnterId);
  const [marketView, setMarketView] = useState<'quests' | 'sell'>('quests');

  const handleVisitMarket = (marketId: string, view: 'quests' | 'sell') => {
    setVisitingMarketId(marketId);
    setMarketView(view);
  };

  // Auto-open target market if specified
  useEffect(() => {
    if (targetMarketId) {
      // Check if market is unlocked
      if (marketManager.isMarketUnlocked(targetMarketId)) {
        setVisitingMarketId(targetMarketId);
        marketManager.visitMarket(targetMarketId);
      }
      // Clear the target market ID
      useGameStore.setState((draft) => {
        draft.targetMarketId = undefined;
      });
    }
  }, [targetMarketId]);

  // If visiting a market with a sell quest, show sell panel
  if (visitingMarketId && activeQuestSell) {
    const questDefinition = marketManager.getQuestDefinition(activeQuestSell);

    if (questDefinition) {
      return (
        <QuestSellPanel
          quest={questDefinition}
          marketId={visitingMarketId}
          onBack={() => {
            useGameStore.setState((draft) => {
              draft.activeQuestSell = undefined;
            });
          }}
        />
      );
    }
  }

  // If visiting a market, show the market interface
  if (visitingMarketId) {
    return (
      <DetailView
        toolbar={
          <div className="border-b border-gray-700">
            <div className="p-4 flex items-center justify-between">
              <BackButton
                label="Markets"
                onClick={() => {
                  setVisitingMarketId(null);
                  setMarketView('quests');
                  useGameStore.setState((draft) => {
                    draft.activeQuestSell = undefined;
                  });
                }}
              />

              {/* View Toggle */}
              <div className="flex gap-3">
                <button
                  onClick={() => setMarketView('quests')}
                  className={`px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${marketView === 'quests'
                    ? 'bg-blue-600 text-white shadow-xl'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                >
                  <span className="text-2xl">📋</span> Quest Board
                </button>
                <button
                  onClick={() => setMarketView('sell')}
                  className={`px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${marketView === 'sell'
                    ? 'bg-green-600 text-white shadow-xl'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                >
                  <span className="text-2xl">💰</span> Sell Fish
                </button>
              </div>
            </div>
          </div>
        }
      >
        {/* Content */}
        {marketView === 'quests' && <QuestBoardView marketId={visitingMarketId} />}
        {marketView === 'sell' && (
          <MarketSellView
            marketId={visitingMarketId}
            onBack={() => setMarketView('quests')}
          />
        )}
      </DetailView>
    );
  }

  // Group markets by zone
  const marketsByZone = ZONE_DEFINITIONS.map((zone) => {
    const zoneMarkets = markets.filter((m) => m.zoneId === zone.id);
    const unlocked = zoneMarkets.filter((m) =>
      marketManager.isMarketUnlocked(m.id)
    );
    const locked = zoneMarkets.filter(
      (m) => !marketManager.isMarketUnlocked(m.id)
    );

    return {
      zone,
      markets: zoneMarkets,
      unlocked,
      locked,
      isZoneUnlocked: unlockedZones.includes(zone.id),
    };
  }).filter((group) => group.markets.length > 0); // Only show zones with markets

  return (
    <ScrollableView gap={8}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">🍽️ Markets</h1>
        <p className="text-gray-400">
          Sell your fish and accept quests from vendors.
        </p>
      </div>

      {/* Markets grouped by zone */}
      {marketsByZone.map(({ zone, markets: _markets, unlocked, locked, isZoneUnlocked }) => (
        <div key={zone.id}>
          {/* Zone Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-white">{zone.name}</h2>
              {!isZoneUnlocked && (
                <span className="text-xs bg-gray-700 text-gray-400 px-2 py-1 rounded">
                  🔒 Zone Locked
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{zone.description}</p>
          </div>

          {/* Unlocked Markets */}
          {unlocked.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Available</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {unlocked.map((market) => (
                  <MarketCard
                    key={market.id}
                    market={market}
                    onVisit={handleVisitMarket}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Locked Markets */}
          {locked.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Locked</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {locked.map((market) => (
                  <MarketCard
                    key={market.id}
                    market={market}
                    onVisit={handleVisitMarket}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {markets.length === 0 && (
        <EmptyState emoji="🍽️" message="No markets available yet." />
      )}
    </ScrollableView>
  );
}
